import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { Pencil } from "lucide-react"
import { useSearchParams } from "react-router"

import {
  useAdminUsers,
  useTikTokAccountsList,
  useAdNetworks,
  useConnectTikTok,
  useRefreshTikTokAccountsList,
  useAssignAccountToUserQuick,
  useUpdateAccountNetwork,
  useDeleteTikTokAccount,
  type TikTokAccountRow,
  type AdminUser,
  type AdNetwork,
} from "@/api/hooks/admin"
import { AdvertiserStatusCell } from "@/components/AdvertiserStatusCell"
import { showToast } from "@/lib/toast"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { NetworkModal } from "./NetworkModal"

const ACCOUNTS_PER_PAGE = 20

type SortableColumn =
  | "advertiser_name"
  | "advertiser_id"
  | "status"
  | "balance"
  | "spend_7d"
  | "network"
  | "buyer"

type FilterOption = "all" | "unassigned" | "unassigned-network"

function accountStatusSortKey(acc: TikTokAccountRow) {
  if (acc.deleted_at) return "0_deleted"
  return (acc.advertiser_status ?? "zzz_unknown").toLowerCase()
}

function AccountStatusCell({ acc }: { acc: TikTokAccountRow }) {
  if (acc.deleted_at) {
    return (
      <span className="inline-flex items-center rounded-full border border-gray-600/40 bg-gray-800/60 px-2 py-0.5 text-xs font-semibold text-gray-300">
        Deleted
      </span>
    )
  }
  return <AdvertiserStatusCell status={acc.advertiser_status} />
}

const sortableThClass =
  "cursor-pointer select-none px-0 py-0 text-left font-semibold text-white hover:bg-[#505050]"

function SortableHeader({
  column,
  children,
  title: headerTitle,
  sortColumn,
  sortDir,
  onSort,
}: {
  column: SortableColumn
  children: ReactNode
  title?: string
  sortColumn: SortableColumn
  sortDir: "asc" | "desc"
  onSort: (column: SortableColumn) => void
}) {
  const active = sortColumn === column
  return (
    <th className={sortableThClass} title={headerTitle}>
      <button
        type="button"
        className="inline-flex w-full max-w-full items-center gap-1 px-4 py-3 text-left font-semibold text-inherit hover:text-white"
        onClick={() => onSort(column)}
      >
        <span>{children}</span>
        {active && (
          <span className="shrink-0 text-[0.65rem] text-[#8b9aff]" aria-hidden>
            {sortDir === "asc" ? "\u25B2" : "\u25BC"}
          </span>
        )}
      </button>
    </th>
  )
}

function InlineEditButton({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-[#a0a0a0] transition-colors hover:bg-[#505050] hover:text-white"
      aria-label={label}
      title={label}
    >
      <Pencil className="h-3.5 w-3.5" />
    </button>
  )
}

export default function AccountsTab() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: accountsData, isLoading, isError, refetch } =
    useTikTokAccountsList()
  const { data: usersData } = useAdminUsers()
  const { data: networksData } = useAdNetworks()
  const connectTikTok = useConnectTikTok()
  const refreshAccounts = useRefreshTikTokAccountsList()
  const assignAccount = useAssignAccountToUserQuick()
  const updateNetwork = useUpdateAccountNetwork()
  const deleteAccount = useDeleteTikTokAccount()

  const [search, setSearch] = useState("")
  const [filter, setFilter] = useState<FilterOption>("all")
  const [sortColumn, setSortColumn] = useState<SortableColumn>("advertiser_name")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")
  const [page, setPage] = useState(1)

  // Assign modal state
  const [assignModalAccount, setAssignModalAccount] =
    useState<TikTokAccountRow | null>(null)
  const [selectedAssignUserId, setSelectedAssignUserId] = useState("")
  const [buyerSearch, setBuyerSearch] = useState("")

  // Network modal state
  const [networkModalAccount, setNetworkModalAccount] =
    useState<TikTokAccountRow | null>(null)
  const [selectedNetwork, setSelectedNetwork] = useState("")

  const users: AdminUser[] = useMemo(() => usersData?.data ?? [], [usersData])
  const networks: AdNetwork[] = useMemo(
    () => networksData?.data ?? [],
    [networksData],
  )
  const rawAccounts: TikTokAccountRow[] = useMemo(
    () => accountsData?.accounts ?? [],
    [accountsData],
  )

  // Handle ?tiktok_connected=true query param
  useEffect(() => {
    if (searchParams.get("tiktok_connected") === "true") {
      showToast("TikTok account connected successfully!")
      refetch()
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, refetch])

  // Enrich accounts with assignment data
  const enrichedAccounts = useMemo(() => {
    const userAccountMap = new Map<
      string,
      { username: string; userId: number }
    >()
    for (const u of users) {
      for (const acc of u.assigned_accounts ?? []) {
        userAccountMap.set(String(acc.advertiser_id), {
          username: u.username,
          userId: u.id,
        })
      }
    }
    return rawAccounts.map((acc) => {
      const assignment = userAccountMap.get(String(acc.advertiser_id))
      return {
        ...acc,
        is_assigned: Boolean(assignment),
        assigned_username: assignment?.username ?? "",
        assigned_user_id: assignment?.userId ?? null,
      }
    })
  }, [rawAccounts, users])

  const networkLabel = useCallback(
    (slug: string | null) => {
      if (!slug) return ""
      const net = networks.find(
        (n) => n.slug.toLowerCase() === slug.toLowerCase(),
      )
      return net?.name ?? slug
    },
    [networks],
  )

  // Filter, sort, paginate
  const processedAccounts = useMemo(() => {
    let result = [...enrichedAccounts]

    // Search
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (acc) =>
          acc.advertiser_name?.toLowerCase().includes(q) ||
          acc.advertiser_id?.toLowerCase().includes(q),
      )
    }

    // Filter
    if (filter === "unassigned") {
      result = result.filter((acc) => !acc.is_assigned)
    } else if (filter === "unassigned-network") {
      result = result.filter((acc) => !acc.network)
    }

    const mult = sortDir === "asc" ? 1 : -1
    result.sort((a, b) => {
      switch (sortColumn) {
        case "advertiser_name":
          return (
            mult *
            (a.advertiser_name ?? "").localeCompare(b.advertiser_name ?? "")
          )
        case "advertiser_id":
          return mult * (a.advertiser_id ?? "").localeCompare(b.advertiser_id ?? "")
        case "status":
          return (
            mult *
            accountStatusSortKey(a).localeCompare(accountStatusSortKey(b))
          )
        case "balance":
          return (
            mult *
            ((Number(a.balance) || 0) - (Number(b.balance) || 0))
          )
        case "spend_7d":
          return (
            mult *
            ((Number(a.spend_7d) || 0) - (Number(b.spend_7d) || 0))
          )
        case "network":
          return (
            mult *
            networkLabel(a.network).localeCompare(networkLabel(b.network))
          )
        case "buyer":
          return mult * (a.assigned_username ?? "").localeCompare(b.assigned_username ?? "")
        default:
          return 0
      }
    })

    return result
  }, [
    enrichedAccounts,
    search,
    filter,
    sortColumn,
    sortDir,
    networkLabel,
  ])

  const totalPages = Math.max(1, Math.ceil(processedAccounts.length / ACCOUNTS_PER_PAGE))
  const paged = processedAccounts.slice(
    (page - 1) * ACCOUNTS_PER_PAGE,
    page * ACCOUNTS_PER_PAGE,
  )

  const handleSortHeader = useCallback((column: SortableColumn) => {
    // Reset page on sort change
    setPage(1)
    setSortColumn((prev) => {
      if (prev === column) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        return prev
      }
      setSortDir(
        column === "balance" || column === "spend_7d" ? "desc" : "asc",
      )
      return column
    })
  }, [])

  function getNetworkName(slug: string | null) {
    if (!slug) return null
    const label = networkLabel(slug)
    return label || null
  }

  function openAssignModal(acc: TikTokAccountRow & { assigned_user_id?: number | null }) {
    setAssignModalAccount(acc)
    setSelectedAssignUserId(
      acc.assigned_user_id != null ? String(acc.assigned_user_id) : "",
    )
  }

  function openNetworkModal(acc: TikTokAccountRow) {
    setNetworkModalAccount(acc)
    setSelectedNetwork(acc.network ?? "")
  }

  function handleConnect() {
    connectTikTok.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.auth_url
      },
      onError: (err) =>
        showToast(
          err instanceof Error ? err.message : "Error connecting",
          "error",
        ),
    })
  }

  function handleRefresh() {
    refreshAccounts.mutate(undefined, {
      onSuccess: (data) => {
        if (data.redirect_url) {
          window.location.href = data.redirect_url
        }
      },
      onError: (err) =>
        showToast(
          err instanceof Error ? err.message : "Error refreshing",
          "error",
        ),
    })
  }

  function handleAssignUser(accountId: number, userId: number) {
    assignAccount.mutate(
      { accountId, userId },
      {
        onSuccess: () => {
          showToast("Account assigned successfully")
          refetch()
        },
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error assigning",
            "error",
          ),
      },
    )
  }

  function handleConfirmAssign() {
    if (!assignModalAccount || !selectedAssignUserId) return
    handleAssignUser(assignModalAccount.id, Number(selectedAssignUserId))
    setAssignModalAccount(null)
    setSelectedAssignUserId("")
    setBuyerSearch("")
  }

  function handleConfirmNetwork() {
    if (!networkModalAccount) return
    updateNetwork.mutate(
      { accountId: networkModalAccount.id, network: selectedNetwork },
      {
        onSuccess: () => {
          showToast("Account network updated")
          setNetworkModalAccount(null)
          refetch()
        },
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error updating network",
            "error",
          ),
      },
    )
  }

  function handleDelete(acc: TikTokAccountRow) {
    if (
      !window.confirm(
        `Are you sure you want to delete "${acc.advertiser_name}"?\n\nThis is a soft delete — the account will be marked as deleted but historical data (campaigns, stats) will be preserved.\n\nThis action cannot be undone from the UI.`,
      )
    )
      return
    deleteAccount.mutate(acc.id, {
      onSuccess: () => {
        showToast(`Account ${acc.advertiser_name} deleted`)
        refetch()
      },
      onError: (err) =>
        showToast(
          err instanceof Error ? err.message : "Error deleting account",
          "error",
        ),
    })
  }

  const assignableUsers = useMemo(
    () =>
      users.filter((u) => u.role === "mediabuyer" || u.role === "team_leader"),
    [users],
  )

  const filteredAssignableUsers = useMemo(() => {
    const q = buyerSearch.trim().toLowerCase()
    if (!q) return assignableUsers
    return assignableUsers.filter((u) => u.username.toLowerCase().includes(q))
  }, [assignableUsers, buyerSearch])

  const closeAssignModal = useCallback(() => {
    setAssignModalAccount(null)
    setSelectedAssignUserId("")
    setBuyerSearch("")
  }, [])

  const sortProps = { sortColumn, sortDir, onSort: handleSortHeader }

  if (isLoading) {
    return (
      <div className="py-8 text-center text-white">
        Loading TikTok accounts...
      </div>
    )
  }

  if (isError) {
    return (
      <div className="py-8 text-center text-red-400">
        Error loading TikTok accounts
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or ID..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="min-w-[200px] flex-1 rounded-lg border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
        />
        <select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as FilterOption)
            setPage(1)
          }}
          className="min-w-[150px] rounded-lg border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
        >
          <option value="all">All Accounts</option>
          <option value="unassigned">Unassigned Users</option>
          <option value="unassigned-network">Unassigned Networks</option>
        </select>
        <button
          className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
          onClick={handleRefresh}
          disabled={refreshAccounts.isPending}
        >
          {"Refresh Accounts"}
        </button>
        <button
          className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
          onClick={handleConnect}
          disabled={connectTikTok.isPending}
        >
          {"Connect TikTok Account"}
        </button>
      </div>

      {/* Accounts table */}
      {rawAccounts.length === 0 ? (
        <div className="py-8 text-center text-white">
          <p>No TikTok accounts connected yet.</p>
          <p className="mt-1 text-sm">
            Click &quot;Connect TikTok Account&quot; to connect via OAuth.
          </p>
        </div>
      ) : processedAccounts.length === 0 ? (
        <div className="py-8 text-center text-white">
          No accounts found matching your filters.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-[#4a4a4a]">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="sticky top-0 z-10 bg-[#444444] text-white">
                  <SortableHeader {...sortProps} column="advertiser_name">
                    Advertiser Name
                  </SortableHeader>
                  <SortableHeader {...sortProps} column="advertiser_id">
                    Advertiser ID
                  </SortableHeader>
                  <SortableHeader
                    {...sortProps}
                    column="status"
                    title="TikTok advertiser status from the last balance refresh (Approved, Suspended, etc.)."
                  >
                    Status
                  </SortableHeader>
                  <SortableHeader {...sortProps} column="balance">Balance</SortableHeader>
                  <SortableHeader
                    {...sortProps}
                    column="spend_7d"
                    title="Sum of synced TikTok spend (daily_metrics.cost) per advertiser for the last 7 UTC calendar days, inclusive."
                  >
                    Spend 7d
                  </SortableHeader>
                  <SortableHeader {...sortProps} column="network">Network</SortableHeader>
                  <SortableHeader {...sortProps} column="buyer">Media Buyer</SortableHeader>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((acc) => {
                  const bal = Number(acc.balance) || 0
                  const spend7d = Number(acc.spend_7d) || 0
                  const networkName = getNetworkName(acc.network)
                  return (
                    <tr
                      key={acc.id}
                      className="border-b border-[#4a4a4a] transition-colors hover:bg-[#444444]"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {acc.advertiser_name}
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-[#2d2d2d] px-1.5 py-0.5 text-xs text-white">
                          {acc.advertiser_id}
                        </code>
                      </td>
                      <td className="px-4 py-3">
                        <AccountStatusCell acc={acc} />
                      </td>
                      <td
                        className={`px-4 py-3 ${bal > 0 ? "text-green-400" : "text-white"}`}
                      >
                        ${bal.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-white">
                        ${spend7d.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1">
                          {networkName ? (
                            <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-900/15 px-2 py-0.5 text-xs font-semibold text-green-400">
                              {networkName}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-yellow-500/30 bg-yellow-900/15 px-2 py-0.5 text-xs font-semibold text-yellow-400">
                              Not assigned
                            </span>
                          )}
                          <InlineEditButton
                            label={
                              networkName ? "Change network" : "Assign network"
                            }
                            onClick={() => openNetworkModal(acc)}
                          />
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1">
                          {acc.assigned_username ? (
                            <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-900/15 px-2 py-0.5 text-xs font-semibold text-green-400">
                              {acc.assigned_username}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-yellow-500/30 bg-yellow-900/15 px-2 py-0.5 text-xs font-semibold text-yellow-400">
                              Unassigned
                            </span>
                          )}
                          <InlineEditButton
                            label={
                              acc.assigned_username
                                ? "Change media buyer"
                                : "Assign media buyer"
                            }
                            onClick={() => openAssignModal(acc)}
                          />
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          className="rounded-md border border-[#d26a78]/35 bg-[#c6596a] px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#b84f60]"
                          onClick={() => handleDelete(acc)}
                        >
                          {"Delete"}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex shrink-0 items-center justify-between border-t border-[#4a4a4a] pt-3">
              <div className="text-sm text-white">
                Showing {(page - 1) * ACCOUNTS_PER_PAGE + 1}-
                {Math.min(page * ACCOUNTS_PER_PAGE, processedAccounts.length)}{" "}
                of {processedAccounts.length} accounts
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:border-[#8b9aff] hover:bg-[#5a616f] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 text-sm text-white">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:border-[#8b9aff] hover:bg-[#5a616f] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assign User Modal */}
      <Dialog
        open={assignModalAccount !== null}
        onOpenChange={(open) => {
          if (!open) closeAssignModal()
        }}
      >
        <DialogContent className="max-w-[500px] border-[#4a4a4a] bg-[#3a3a3a] text-white">
          <DialogHeader>
            <DialogTitle>
              {assignModalAccount?.assigned_username
                ? "Change media buyer"
                : "Assign media buyer"}
            </DialogTitle>
            <DialogDescription className="text-white">
              Select a media buyer for this account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">Account</label>
              <div className="rounded-md bg-[#2d2d2d] px-3 py-2">
                <strong>{assignModalAccount?.advertiser_name}</strong>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Media buyer</label>
              <input
                type="text"
                value={buyerSearch}
                onChange={(e) => setBuyerSearch(e.target.value)}
                placeholder="Search buyers..."
                className="mb-2 w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white placeholder:text-[#888]"
              />
              <select
                value={selectedAssignUserId}
                onChange={(e) => setSelectedAssignUserId(e.target.value)}
                className="w-full rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
                size={Math.min(8, Math.max(4, filteredAssignableUsers.length + 1))}
              >
                <option value="">-- Select user --</option>
                {filteredAssignableUsers.length === 0 ? (
                  <option value="" disabled>
                    {buyerSearch.trim()
                      ? "No buyers match your search"
                      : "No buyers available"}
                  </option>
                ) : (
                  filteredAssignableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.username}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleConfirmAssign}
                disabled={!selectedAssignUserId || assignAccount.isPending}
                className="flex-1 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={closeAssignModal}
                className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Assign Network Modal */}
      <NetworkModal
        account={networkModalAccount}
        networks={networks}
        selectedNetwork={selectedNetwork}
        onSelectNetwork={setSelectedNetwork}
        onConfirm={handleConfirmNetwork}
        onClose={() => setNetworkModalAccount(null)}
        isPending={updateNetwork.isPending}
        getNetworkName={getNetworkName}
      />
    </div>
  )
}
